"use client";

import { useState } from 'react';
import styles from "./contact.module.css";

export default function ContactForm({ translations }) {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        message: '',
        type: 'individual' // 'individual' or 'company'
    });

    const [status, setStatus] = useState({
        submitting: false,
        success: false,
        error: null
    });

    const [errors, setErrors] = useState({});

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = translations.errors.nameRequired;
        if (!formData.message.trim()) newErrors.message = translations.errors.messageRequired;

        // Conditional validation
        if (formData.type === 'individual') {
            if (!formData.phone.trim()) newErrors.phone = translations.errors.phoneRequired;
        } else if (formData.type === 'company') {
            // Basic email validation
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!formData.email.trim() || !emailRegex.test(formData.email)) {
                newErrors.email = translations.errors.emailRequired;
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setStatus({ submitting: true, success: false, error: null });

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            const data = await response.json();

            if (response.ok) {
                setStatus({ submitting: false, success: true, error: null });
                setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    message: '',
                    type: formData.type // Keep the current type selection
                });
                setTimeout(() => setStatus(prev => ({ ...prev, success: false })), 5000);
            } else {
                throw new Error(data.message || 'Something went wrong');
            }
        } catch (error) {
            setStatus({
                submitting: false,
                success: false,
                error: translations.error
            });
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    return (
        <form className={styles.form} onSubmit={handleSubmit}>
            {status.success && (
                <div className={styles.success}>
                    {translations.success}
                </div>
            )}

            {status.error && (
                <div className={styles.errorMsg}>
                    {status.error}
                </div>
            )}

            <div className={styles.typeSelector}>
                <label className={formData.type === 'individual' ? styles.activeType : ''}>
                    <input
                        type="radio"
                        name="type"
                        value="individual"
                        checked={formData.type === 'individual'}
                        onChange={handleChange}
                    />
                    {translations.individual}
                </label>
                <label className={formData.type === 'company' ? styles.activeType : ''}>
                    <input
                        type="radio"
                        name="type"
                        value="company"
                        checked={formData.type === 'company'}
                        onChange={handleChange}
                    />
                    {translations.company}
                </label>
            </div>

            <div className={styles.formGroup}>
                <label htmlFor="name" className={styles.label}>{translations.fullName}</label>
                <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={translations.placeholderName}
                    className={styles.input}
                    disabled={status.submitting}
                />
                {errors.name && <span className={styles.error}>{errors.name}</span>}
            </div>

            <div className={styles.formGroup}>
                <label htmlFor="email" className={styles.label}>
                    {translations.email} {formData.type === 'individual' && <span className="text-gray-400 text-xs font-normal">(Optional)</span>}
                </label>
                <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={translations.placeholderEmail}
                    className={styles.input}
                    disabled={status.submitting}
                />
                {errors.email && <span className={styles.error}>{errors.email}</span>}
            </div>

            <div className={styles.formGroup}>
                <label htmlFor="phone" className={styles.label}>
                    {translations.phone} {formData.type === 'company' && <span className="text-gray-400 text-xs font-normal">(Optional)</span>}
                </label>
                <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder={translations.placeholderPhone}
                    className={styles.input}
                    disabled={status.submitting}
                />
                {errors.phone && <span className={styles.error}>{errors.phone}</span>}
            </div>

            <div className={styles.formGroup}>
                <label htmlFor="message" className={styles.label}>{translations.message}</label>
                <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={translations.placeholderMessage}
                    className={styles.textarea}
                    disabled={status.submitting}
                ></textarea>
                {errors.message && <span className={styles.error}>{errors.message}</span>}
            </div>

            <button
                type="submit"
                className={styles.submitBtn}
                disabled={status.submitting}
            >
                {status.submitting ? translations.sending : translations.sendMessage}
            </button>
        </form>
    );
}
